import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  DialogActions,
  Grid, // ✅ added Grid for layout
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import QRCode from "qrcode";
import { usePermissions } from "../login/PermissionContext";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

function countResolvedServiceNames(rows) {
  if (!Array.isArray(rows)) return -1;
  let c = 0;
  for (const row of rows) {
    const raw =
      row?.item_name ??
      row?.itemName ??
      row?.service_name ??
      row?.name ??
      "";
    const t = String(raw).trim();
    if (t && t !== "--" && t !== "-") c += 1;
  }
  return c;
}

function pickInvoiceLineItems(data) {
  if (!data) return [];
  const candidates = [
    data.itemsList,
    data.invoice_items,
    data.invoiceItems,
    data.items,
  ].filter((a) => Array.isArray(a) && a.length > 0);
  if (candidates.length === 0) return [];
  let best = candidates[0];
  let bestScore = countResolvedServiceNames(best);
  for (let i = 1; i < candidates.length; i += 1) {
    const arr = candidates[i];
    const sc = countResolvedServiceNames(arr);
    if (sc > bestScore) {
      best = arr;
      bestScore = sc;
    }
  }
  return best;
}

function buildServiceNameByIdMap(serviceMaster = []) {
  const map = new Map();
  if (!Array.isArray(serviceMaster)) return map;
  serviceMaster.forEach((s) => {
    const key = s?.medical_service_id ?? s?.id;
    const value = s?.name ?? s?.item_name ?? "";
    if (key != null && String(value).trim()) {
      map.set(String(key), String(value).trim());
    }
  });
  return map;
}

function enrichLineItemsWithServiceNames(rows, serviceMap) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const existingName = String(
      row?.item_name ?? row?.itemName ?? row?.service_name ?? "",
    ).trim();
    if (existingName && existingName !== "--" && existingName !== "-") {
      return row;
    }
    const serviceId = row?.items ?? row?.item_id ?? row?.service_id;
    const resolved =
      serviceId != null ? serviceMap.get(String(serviceId)) || "" : "";
    if (!resolved) return row;
    return {
      ...row,
      item_name: resolved,
      itemName: resolved,
    };
  });
}

export default function InvoicePayment({ open, onClose, invoice, onAfterClose, disableClose = false, onPaymentSuccess ,  allowPartialPayment = true,
  useRefundPaymentApi = false,}) {
  const { userInfo } = usePermissions();
  const history = useHistory();

  const isMounted = useRef(false);
  const paymentControllerRef = useRef(null);

  const [paymentType, setPaymentType] = useState("UPI");
  const [isPaid, setIsPaid] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState("0.00");
  const [paymentMode, setPaymentMode] = useState("FULL"); // "FULL" or "PARTIAL"
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [qrAmount, setQrAmount] = useState("0.00");
  const [qrUrl, setQrUrl] = useState("");
  const [transactionId, setTransactionId] = useState("");

  const upiId = userInfo?.upi_id || "";
  const REACT_APP_BACKEND_ROUTE_URL =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "";
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const isTxnRequired = paymentType !== "CASH";

  const headers = {
    Authorization: `Bearer ${authToken}`,
    "App-Mode": sessionStorage.getItem("appMode"),
    "Org-Code": sessionStorage.getItem("orgCode"),
    Username: sessionStorage.getItem("username"),
    "User-Access-Key": sessionStorage.getItem("accessKey"),
  };

  // ✅ FIXED: invoiceData should come directly from invoice prop
  const invoiceData = invoice || null;
  const getCurrentUserId = () => {
    try {
      const token = authToken || sessionStorage.getItem("accessToken") || "";
      const parts = token.split(".");
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
      const raw = payload?.user_id ?? payload?.userId;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  };

  const serviceMaster = invoice?.serviceMaster || [];
  const patientMaster = invoice?.patientMaster || [];
  const isSettlementPayment = (payment) => {
    const paymentType = String(payment?.payment_type || "").toUpperCase();
    if (paymentType === "REFUND") return false;
    const status = String(payment?.status || "").toUpperCase();
    const blocked = new Set([
      "FAILED",
      "CANCELLED",
      "VOID",
      "REJECTED",
      "REVERSED",
    ]);
    if (blocked.has(status)) return false;
    if (
      status === "" ||
      status === "SUCCESS" ||
      status === "PARTIALLY PAID" ||
      status === "PARTIALLY_PAID" ||
      status === "PAID" ||
      status === "PENDING" ||
      status === "COMPLETED"
    ) {
      return true;
    }
    if (
      paymentType === "PARTIAL" ||
      paymentType === "FULL" ||
      paymentType === "PARTIAL PAYMENT"
    ) {
      return true;
    }
    return false;
  };

  /* ===============================
    MOUNT / UNMOUNT SAFE
  ============================== */
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (paymentControllerRef.current) paymentControllerRef.current.abort();
    };
  }, []);

  // Calculate balances
  const totalAmt = Number(invoiceData?.total_amount ?? invoiceData?.total ?? invoiceData?.amount ?? invoiceData?.payable_amount ?? 0);
  
  // Calculate paidAmt from payment_details if partial_payment is not sufficient / null
  let defaultPaidAmt = Number(invoiceData?.partial_payment ?? invoiceData?.partialPayment ?? 0);
  if (invoiceData?.payment_details && invoiceData.payment_details.length > 0) {
    const settlementPayments = invoiceData.payment_details.filter(isSettlementPayment);
    const sumFromDetails = settlementPayments.reduce((sum, p) => sum + Number(p.payment_amount || 0), 0);
    if (sumFromDetails > defaultPaidAmt) {
      defaultPaidAmt = sumFromDetails;
    }
  }
  const paidAmt = defaultPaidAmt;
  
  const outstandingAmount = Math.max(0, totalAmt - paidAmt).toFixed(2);
  const isAmountInvalid =
    allowPartialPayment &&
    paymentMode === "PARTIAL" &&
    (Number(paymentAmount) <= 0 || Number(paymentAmount) > Number(outstandingAmount));

  useEffect(() => {
    if (!allowPartialPayment && paymentMode !== "FULL") {
      setPaymentMode("FULL");
    }
  }, [allowPartialPayment, paymentMode]);

  // invoice amount set
  useEffect(() => {
    if (open && invoiceData && isMounted.current) {
      const formattedAmt = Number(outstandingAmount).toFixed(2);
      setPaymentAmount(formattedAmt);
      setQrAmount(formattedAmt);
    }
  }, [open, invoiceData, outstandingAmount]);

  // status set
  useEffect(() => {
    if (open && invoiceData && isMounted.current) {
      setIsPaid(Number(outstandingAmount) <= 0.01);
    }
  }, [open, invoiceData, outstandingAmount]);

  // Debounce paymentAmount for QR generation
  useEffect(() => {
    if (!open) return;

    if (paymentMode === "FULL") {
      const formattedAmt = Number(outstandingAmount).toFixed(2);
      setPaymentAmount(formattedAmt);
      setQrAmount(formattedAmt);
      setIsGeneratingQR(false);
      return;
    }

    // For partial payment, implement debounce
    setIsGeneratingQR(true);
    const handler = setTimeout(() => {
      setQrAmount(paymentAmount);
      setIsGeneratingQR(false);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [paymentAmount, paymentMode, open, invoiceData]);

  // QR generation
  useEffect(() => {
    if (open && paymentType === "UPI" && upiId && !isGeneratingQR && !useRefundPaymentApi) {
      const upiString = `upi://pay?pa=${encodeURIComponent(
        upiId
      )}&cu=INR&am=${encodeURIComponent(qrAmount || "0.00")}`;

      QRCode.toDataURL(upiString, { width: 256, margin: 1 })
        .then((url) => {
          if (isMounted.current) setQrUrl(url);
        })
        .catch(() => {
          if (isMounted.current) setQrUrl("");
        });
    } else {
      if (isMounted.current) setQrUrl("");
    }
  }, [open, paymentType, qrAmount, upiId, isGeneratingQR, useRefundPaymentApi]);

  const handleClose = useCallback(() => {
    if (disableClose) return;
    if (typeof onClose === "function") onClose();
    if (typeof onAfterClose === "function") onAfterClose();
  }, [onClose, onAfterClose, disableClose]);

  // ✅ Txn generator (safe)
  const generateTxnRef = () => {
    const inv =
      invoiceData?.invoiceNumber ||
      invoiceData?.invoice_number ||
      invoiceData?.invoiceNo ||
      "";
    const invoiceLast = String(inv).slice(-3) || "000";
    return `UPI-INV-${invoiceLast}-${Date.now()}`;
  };

  const buildReceiptViewData = (invoiceNo, payment) => {
    const items =
      invoiceData?.items ||
      invoiceData?.itemsList ||
      invoiceData?.invoice_items ||
      invoiceData?.invoiceItems ||
      [];
    if (useRefundPaymentApi) {
      const paid =
        payment?.payment_amount != null ? payment.payment_amount : paymentAmount || "0.00";
      return {
        ...invoiceData,
        invoice_number: invoiceData?.invoice_number || invoiceData?.invoiceNumber || invoiceNo,
        patient_name: invoiceData?.patient_name || invoiceData?.patientName || "N/A",
        patient_id: invoiceData?.patient_id ?? invoiceData?.patient_record_id,
        refund_number:
          payment?.refund_number ||
          invoiceData?.refund_number ||
          payment?.receipt_number ||
          "N/A",
        receipt_no: payment?.receipt_number || invoiceData?.refund_number || "N/A",
        approved_at:
          invoiceData?.approved_at ||
          payment?.approved_at ||
          payment?.created_at ||
          "N/A",
        settlement_status: payment?.settlement_status || invoiceData?.settlement_status || "SETTLED",
        itemsList: (Array.isArray(items) ? items : []).map((it) => ({
          item_name: it?.item_name || it?.service_name || it?.name || "—",
          approved_quantity: Number(it?.approved_quantity ?? it?.quantity ?? 0) || 0,
          quantity: Number(it?.approved_quantity ?? it?.quantity ?? 0) || 0,
          item_price: it?.item_price ?? it?.price,
          approved_amount: it?.approved_amount,
          subtotal: it?.approved_amount ?? it?.subtotal,
        })),
        payment_status: "PAID",
        payment_date: payment?.created_at || "N/A",
        payment_mode: payment?.mode_of_payment || paymentType || "N/A",
        ref_no: payment?.transaction_ref_no || "N/A",
        amount_paid: paid,
        refund_amount: paid,
        payment_details: Array.isArray(payment) ? payment : [payment],
      };
    }
    return {
      ...invoiceData,
      invoice_number: invoiceData?.invoice_number || invoiceData?.invoiceNumber || invoiceNo,
      patient_name: invoiceData?.patient_name || invoiceData?.patientName || "N/A",
      itemsList: Array.isArray(items) ? items : [],
      payment_status: "PAID",
      payment_date: payment?.created_at || "N/A",
      receipt_no: payment?.receipt_number || "N/A",
      payment_mode: payment?.mode_of_payment || paymentType || "N/A",
      ref_no: payment?.transaction_ref_no || "N/A",
      amount_paid: payment?.payment_amount || paymentAmount || "0.00",
      payment_details: Array.isArray(payment) ? payment : [payment],
    };
  };

  // ✅ Mark as Paid handler
  const updatePaymentStatus = useCallback(async () => {
    try {
      const invoiceNo =
        invoiceData?.invoiceNumber ||
        invoiceData?.invoice_number ||
        invoiceData?.invoiceNo;

      if (!invoiceNo) {
        toast.error("Invoice number missing");
        return;
      }
      if (useRefundPaymentApi && !invoiceData?.refund_request_id) {
        toast.error("Invalid refund reference");
        return;
      }
      if (isTxnRequired && !transactionId.trim()) {
        toast.error("Transaction ID is required for UPI/Card payments");
        return;
      }

      if (Number(paymentAmount) <= 0) {
        toast.error("Payment amount must be greater than 0");
        return;
      }

      if (Number(paymentAmount) > Number(outstandingAmount)) {
        toast.error(`Payment amount (₹${paymentAmount}) cannot exceed the outstanding amount (₹${outstandingAmount})`);
        return;
      }

      if (paymentControllerRef.current) paymentControllerRef.current.abort();

      const controller = new AbortController();
      paymentControllerRef.current = controller;

      if (isMounted.current) setIsUpdatingPayment(true);

      const currentUserId = getCurrentUserId();
      const payload = useRefundPaymentApi
        ? {
            refund_request_id: invoiceData?.refund_request_id,
            invoice_no: invoiceNo,
            payment_amount: paymentAmount,
            currency: "INR",
            mode_of_payment: paymentType,
            remarks: "Refund payout",
            ...(transactionId && { transaction_ref_no: transactionId }),
            ...(invoiceData?.approved_by_id != null && {
              approved_by: Number(invoiceData.approved_by_id),
            }),
            ...(currentUserId != null && { created_by: currentUserId }),
          }
        : {
            invoice_no: invoiceNo,
            payment_amount: paymentAmount,
            currency: "INR",
            payment_type: paymentMode,
            mode_of_payment: paymentType,
            status: "PENDING",
            remarks: "Invoice payment", //remarks is not required in the payload
            ...(transactionId && { transaction_ref_no: transactionId }),
            ...(invoiceData?.care_context_id && { care_context_id: invoiceData.care_context_id }),
          };

      const resp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}${useRefundPaymentApi ? "refund_payment/" : "payment_details/"}`,
        payload,
        { headers, signal: controller.signal }
      );

      if (!isMounted.current) return;

      if (resp?.data?.status === "Success") {
        // Call the onPaymentSuccess callback if provided (e.g. to update lab test status)
        if (typeof onPaymentSuccess === "function") {
          onPaymentSuccess();
        }
        if (useRefundPaymentApi) {
          const payment = resp?.data?.data || {};
          const receiptViewData = buildReceiptViewData(invoiceNo, payment);
          history.push(`/refundReceipt/${receiptViewData.invoice_number}`, {
            data: receiptViewData,
            serviceMaster,
            patientMaster,
          });
          toast.success(resp?.data?.message || "Refund payout recorded successfully.");
          if (typeof onClose === "function") onClose();
          if (typeof onAfterClose === "function") onAfterClose();
          return;
        }

        const payment = resp?.data?.data || {};

        const paymentMapped = {
          payment_date: payment?.created_at || "N/A",
          receipt_no: payment?.receipt_number || "N/A",
          payment_mode: payment?.mode_of_payment || paymentType || "N/A",
          ref_no: payment?.transaction_ref_no || "N/A",
          amount_paid: payment?.payment_amount || paymentAmount || "0.00",
        };

        const serviceNameMap = buildServiceNameByIdMap(serviceMaster);
        const items = enrichLineItemsWithServiceNames(
          pickInvoiceLineItems(invoiceData),
          serviceNameMap,
        );

        // ✅ FINAL viewInvoiceData
        const viewInvoiceData = {
          ...invoiceData,

          // ✅ ViewInvoice expects invoice_number
          invoice_number:
            invoiceData?.invoiceNumber ||
            invoiceData?.invoice_number ||
            invoiceNo,

          // ✅ ViewInvoice expects patient_name
          patient_name:
            invoiceData?.patient_name ||
            invoiceData?.patientName ||
            invoiceData?.patient_name ||
            "N/A",

          // ✅ ViewInvoice will use itemsList / items
          itemsList: Array.isArray(items) ? items : [],

          // ✅ Facility Address mapping (from invoiceData)
          facility_address: invoiceData?.facility_address || "",
          center_address: invoiceData?.center_address || "",
          center: invoiceData?.center || "",
          facility_name: invoiceData?.facility_name || "",

          payment_status: resp?.data?.data?.payment_status || resp?.data?.payment_status || "PAID",
          ...paymentMapped,
          payment,
        };

        // Fetch all payments for this invoice so ViewInvoice gets the full list
        let allPaymentDetails = [];
        let lineItemsFromServer = [];
        try {
          const paymentsResp = await axios.post(
            `${REACT_APP_BACKEND_ROUTE_URL}get_all_invoices/`,
            { hip_id: sessionStorage.getItem("facility_name") || "", is_pagination_required: false },
            { headers }
          );
          const allInvoices = Array.isArray(paymentsResp?.data?.invoices) ? paymentsResp.data.invoices : [];
          const matchingInvoice = allInvoices.find(inv => inv.invoice_number === invoiceNo || inv.invoice_id === invoiceData?.invoice_id);
          if (matchingInvoice) {
            allPaymentDetails = Array.isArray(matchingInvoice.payment_details)
              ? matchingInvoice.payment_details
              : Array.isArray(matchingInvoice.payments)
                ? matchingInvoice.payments
                : [];
            lineItemsFromServer = enrichLineItemsWithServiceNames(
              pickInvoiceLineItems(matchingInvoice),
              serviceNameMap,
            );
          }
        } catch (fetchErr) {
          console.warn("Could not fetch full payment list, using single payment:", fetchErr);
        }

        // If we couldn't fetch all payments, fall back to merging from invoiceData + new payment
        if (!allPaymentDetails.length) {
          const existingPayments = Array.isArray(invoiceData?.payment_details)
            ? invoiceData.payment_details
            : Array.isArray(invoiceData?.payments)
              ? invoiceData.payments
              : invoiceData?.payment
                ? [invoiceData.payment]
                : [];
          allPaymentDetails = [...existingPayments, payment];
        }

        // ✅ push with masters (NO sessionStorage required)
        history.push(`/ViewInvoice/${viewInvoiceData.invoice_number}`, {
          data: {
            ...viewInvoiceData,
            itemsList: lineItemsFromServer.length > 0 ? lineItemsFromServer : viewInvoiceData.itemsList,
            payment_details: allPaymentDetails,
          },
          serviceMaster,
          patientMaster,
        });

        // Close modal after successful payment (bypass disableClose)
        if (typeof onClose === "function") onClose();
        if (typeof onAfterClose === "function") onAfterClose();
      } else {
        toast.error(resp?.data?.message || "Payment failed");
      }
    } catch (e) {
      if (!isMounted.current) return;
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;

      console.log("Payment API Error:", e);
      console.log("Backend response:", e?.response?.data);

      toast.error(
        (() => {
          const backendMsg =
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            "";
          if (useRefundPaymentApi) {
            if (backendMsg.includes("Invalid refund_request_id")) return "Invalid refund reference";
            if (backendMsg.includes("Only APPROVED refund requests can be paid"))
              return "Refund not approved yet";
            if (backendMsg.includes("already settled")) return "Payment already completed";
            if (backendMsg.includes("does not belong to the given invoice_no"))
              return "Invoice and refund mismatch";
          }
          return backendMsg || "Internal server error";
        })()
      );
    } finally {
      if (isMounted.current) setIsUpdatingPayment(false);
      paymentControllerRef.current = null;
    }
  }, [
    invoiceData,
    paymentAmount,
    paymentMode,
    outstandingAmount,
    paymentType,
    REACT_APP_BACKEND_ROUTE_URL,
    headers,
    history,
    onClose,
    onAfterClose,
    serviceMaster,
    patientMaster,
    transactionId,
    isTxnRequired,
    useRefundPaymentApi,
    paymentAmount,
    paymentType,
  ]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      disableEscapeKeyDown={disableClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "90%",
          maxWidth: "600px",
          height: "auto",
          zIndex: 9997,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2,
          color: "#fff",
        }}
      >
        Process Invoice Payment
        {!disableClose && (
          <IconButton onClick={handleClose}>
            <CloseRoundedIcon sx={{ color: "#fff" }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 2.5, maxHeight: "calc(80vh - 100px)", overflowY: "auto" }}
      >
        {/* ===== I am converting Box layout into MUI Grid layout ===== */}

        <Grid container spacing={2}>
          {!invoiceData?.payment_status ||
            ["UNPAID", "PARTIALLY PAID", "PENDING"].includes(invoiceData?.payment_status?.toUpperCase()) ? (
            <>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    p: 2,
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: 10,
                      bgcolor: "white",
                      px: 0.5,
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    Payment Mode
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: 500 }}>
                      <input
                        type="radio"
                        name="paymentMode"
                        value="FULL"
                        checked={paymentMode === "FULL"}
                        onChange={() => setPaymentMode("FULL")}
                        style={{ marginRight: 8, width: 18, height: 18 }}
                      />
                      Full Payment
                    </label>
                    {allowPartialPayment && (
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: 500 }}>
                        <input
                          type="radio"
                          name="paymentMode"
                          value="PARTIAL"
                          checked={paymentMode === "PARTIAL"}
                          onChange={() => setPaymentMode("PARTIAL")}
                          style={{ marginRight: 8, width: 18, height: 18 }}
                        />
                        Partial Payment
                      </label>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={4}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Payment Type
                  </label>

                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="form-control"
                  >
                    <option value="UPI">UPI</option>
                    <option value="CASH">Cash</option>
                    <option value="CREDIT CARD">Credit Card</option>
                    <option value="DEBIT CARD">Debit Card</option>
                    <option value="BANK TRANSFER">Bank Transfer</option>
                  </select>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Payment Amount
                  </label>

                  <input
                    type="number"
                    value={paymentAmount}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPaymentAmount(val);
                    }}
                    onBlur={() => {
                      if (
                        allowPartialPayment &&
                        paymentMode === "PARTIAL" &&
                        Number(paymentAmount) > Number(outstandingAmount)
                      ) {
                        toast.warning(
                          `Payment amount (₹${paymentAmount}) cannot exceed outstanding (₹${outstandingAmount}).`,
                        );
                      }
                    }}
                    className="form-control"
                    disabled={paymentMode === "FULL"}
                    readOnly={paymentMode === "FULL"}
                    style={paymentMode === "FULL" ? { backgroundColor: "#eff6ff", border: "1px solid #bfdbfe" } : {}}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                    Payment Status
                  </label>

                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: "8px",
                      fontSize: 14,
                      fontWeight: 600,
                      color: isPaid
                        ? "#065f46"
                        : paidAmt >= 1
                          ? "#9a3412"
                          : "#991b1b",
                      backgroundColor: isPaid
                        ? "#d1fae5"
                        : paidAmt >= 1
                          ? "#ffedd5"
                          : "#fee2e2",
                      width: "fit-content",
                    }}
                  >
                    {isPaid ? "Paid" : paidAmt >= 1 ? "Partially Paid" : "Pending"}
                  </Box>
                </Grid>
              </Grid>

              {/* Balance Summary Row */}
              <Grid container item xs={12} spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Total Bill
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ₹{totalAmt.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Already Paid
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                    ₹{paidAmt.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Outstanding
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "error.main" }}>
                    ₹{outstandingAmount}
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Transaction ID {isTxnRequired && <span style={{ color: "red" }}>*</span>}
                </label>

                <input
                  type="text"
                  placeholder={isTxnRequired ? "Enter Transaction ID" : "Transaction ID"}
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="form-control"
                />
              </Grid>

              {paymentType === "UPI" && !useRefundPaymentApi && (
                <Grid item xs={12}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      p: 2,
                    }}
                  >
                    <Grid item xs={12} sm={4}>
                      {!upiId ? (
                        <Box
                          sx={{
                            width: 180,
                            height: 180,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "text.secondary"
                          }}
                        >
                          QR Code Not Available
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 180,
                            height: 180,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: isGeneratingQR ? "#f9fafb" : "transparent",
                            borderRadius: "8px",
                            border: isGeneratingQR ? "1px dashed #d1d5db" : "none"
                          }}
                        >
                          {isGeneratingQR ? "Loading..." : qrUrl ? (
                            <img
                              src={qrUrl}
                              alt="UPI QR Code"
                              style={{ width: "100%", height: "100%" }}
                            />
                          ) : (
                            "QR Code Not Available"
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <div style={{ marginBottom: "8px", fontWeight: 600, fontSize: "16px" }}>
                        Pay invoice via UPI
                      </div>
                      <div style={{ marginBottom: "4px", color: "#4b5563" }}>
                        UPI ID: {upiId || "Not Available"}
                      </div>
                      <div style={{ fontWeight: 500 }}>Amount: ₹{isGeneratingQR ? "..." : qrAmount}</div>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          ) : (invoiceData?.payment_status?.toUpperCase() === "PAID" || isPaid) ? (
            <Typography>This invoice has already been fully paid.</Typography>
          ) : (
            <Typography>
              Refund process status: {invoiceData?.payment_status}
            </Typography>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <button
            type="button"
            onClick={updatePaymentStatus}
            className="btn btn__secondary btn__rounded"
            disabled={isUpdatingPayment || isPaid || isAmountInvalid}
          >
            {isUpdatingPayment ? "Updating..." : "Mark as Paid"}
          </button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
